import mammoth from 'mammoth';

import * as pdfjsLib from 'pdfjs-dist/build/pdf';

pdfjsLib.GlobalWorkerOptions.workerSrc = `https://cdnjs.cloudflare.com/ajax/libs/pdf.js/${pdfjsLib.version}/pdf.worker.min.js`;


// Determines the file type based on MIME type or file extension
const getFileType = (file) => {
  if (file.type) {
    if (file.type.startsWith('image/')) return 'image';
    if (file.type === 'application/pdf') return 'pdf';
    if (file.type === 'application/vnd.openxmlformats-officedocument.wordprocessingml.document') return 'docx';
    if (file.type === 'text/plain') return 'txt';
  }
  const ext = file.name.split('.').pop().toLowerCase();
  if (['jpg', 'jpeg', 'png', 'gif', 'bmp', 'webp'].includes(ext)) return 'image';
  if (ext === 'pdf') return 'pdf';
  if (ext === 'docx') return 'docx';
  if (ext === 'txt') return 'txt';
  return null;
};

// Converts a Data URI to a Blob object
const dataURItoBlob = (dataURI) => {
  const byteString = atob(dataURI.split(',')[1]);
  const mimeString = dataURI.split(',')[0].split(':')[1].split(';')[0];
  const ab = new ArrayBuffer(byteString.length);
  const ia = new Uint8Array(ab);
  for (let i = 0; i < byteString.length; i++) {
    ia[i] = byteString.charCodeAt(i);
  }
  return new Blob([ab], { type: mimeString });
};

// Compresses an image to a specified maximum size (default 0.8MB)
const compressImage = async (file, maxSizeMB = 0.8) => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.readAsDataURL(file);
    reader.onload = (e) => {
      const img = new Image();
      img.src = e.target.result;
      img.onload = () => {
        const canvas = document.createElement('canvas');
        let width = img.width;
        let height = img.height;
        const maxPixels = 2000 * 2000;
        if (width * height > maxPixels) {
          const scale = Math.sqrt(maxPixels / (width * height));
          width = Math.floor(width * scale);
          height = Math.floor(height * scale);
        }
        canvas.width = width;
        canvas.height = height;
        const ctx = canvas.getContext('2d');
        ctx.drawImage(img, 0, 0, width, height);
        let quality = 0.9;
        let dataUrl = canvas.toDataURL('image/jpeg', quality);
        let blob = dataURItoBlob(dataUrl);
        while (blob.size > maxSizeMB * 1024 * 1024 && quality > 0.1) {
          quality -= 0.1;
          dataUrl = canvas.toDataURL('image/jpeg', quality);
          blob = dataURItoBlob(dataUrl);
        }
        resolve(dataUrl);
      };
      img.onerror = reject;
    };
    reader.onerror = reject;
  });
};

// Extracts text from a PDF file using PDF.js
const extractPdfText = async (file) => {
  try {
    // console.log("Starting PDF extraction for file:", file.name);
    const arrayBuffer = await file.arrayBuffer();
    // console.log("ArrayBuffer loaded, size:", arrayBuffer.byteLength);
    const pdf = await pdfjsLib.getDocument({ data: arrayBuffer }).promise;
    // console.log("PDF document loaded, number of pages:", pdf.numPages);
    let fullText = '';
    for (let i = 1; i <= pdf.numPages; i++) {
      const page = await pdf.getPage(i);
      const textContent = await page.getTextContent();
      const pageText = textContent.items.map(item => item.str).join(' ');
      fullText += pageText + '\n';
    }
    // console.log("PDF text extracted successfully:", fullText);
    return fullText;
  } catch (error) {
    // console.error("Error in extractPdfText:", error);
    throw new Error(`Failed to extract text from PDF: ${error.message}`);
  }
};

// Extracts text from a DOCX file using Mammoth
const extractDocxText = async (file) => {
  const arrayBuffer = await file.arrayBuffer();
  const result = await mammoth.extractRawText({ arrayBuffer });
  // console.log("docx text extracted successfully:", result.value);
  return result.value;
};

// Reads the content of a plain text file
const readAsText = (file) => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result);
    reader.onerror = reject;
    reader.readAsText(file);
  });
};

// Parses a file based on its type and returns a structured object
const parseFile = async (file) => {
  const fileType = getFileType(file);
  if (!fileType) throw new Error('Unsupported file type. Please upload an image, PDF, DOCX, or TXT file.');
  if (fileType === 'image') {
    const dataUrl = await compressImage(file);
    return { type: 'image', name: file.name, data: dataUrl };
  } else if (fileType === 'pdf') {
    const text = await extractPdfText(file);
    return { type: 'pdf', name: file.name, data: text };
  } else if (fileType === 'docx') {
    const text = await extractDocxText(file);
    return { type: 'docx', name: file.name, data: text };
  } else if (fileType === 'txt') {
    const text = await readAsText(file);
    return { type: 'txt', name: file.name, data: text };
  }
};

// Opens a file picker dialog for selecting images and documents
const openFilePicker = () => {
  const input = document.createElement('input');
  input.type = 'file';
  input.accept = 'image/*,.pdf,.doc,.docx,.txt';
  input.onchange = (e) => {
    if (e.target.files && e.target.files[0]) {
      const file = e.target.files[0];
      console.log('File selected:', file.name);
      // You can call parseFile(file) here and handle the result as needed
    }
  };
  input.click();
};

// Export all functions for use in other modules
export {
  getFileType,
  dataURItoBlob,
  compressImage,
  extractPdfText,
  extractDocxText,
  readAsText,
  parseFile,
  openFilePicker
};